@if (!started()) {
  <app-dairy-yield-scan-cover (startScan)="startScan()"></app-dairy-yield-scan-cover>
} @else {
  <main class="scan-page" [agrBlockSpinner]="loading()">
    @if (loadFailed()) {
      <section class="status-panel" data-test="questionnaire-load-error">
        <agr-extended-notification
          [notificationType]="errorNotificationType"
          [title]="'dairyYieldScan.loadError.title' | translate"
          [message]="'dairyYieldScan.loadError.message' | translate"
          [actionText]="'dairyYieldScan.loadError.retry' | translate"
          (action)="loadQuestionnaire()"
        ></agr-extended-notification>
      </section>
    } @else if (questionnaire(); as questionnaire) {
      <nav
        class="scan-progress"
        [attr.aria-label]="'dairyYieldScan.progress.navigationLabel' | translate"
        [style.--scan-progress]="progress() + '%'"
      >
        <span class="progress-track" aria-hidden="true"><span></span></span>
        <button
          type="button"
          class="progress-step"
          [class.active]="activeProgressStep() === 0"
          [attr.aria-current]="activeProgressStep() === 0 ? 'step' : null"
          (click)="scrollToSection('scan-details')"
        >
          <span class="progress-dot">0</span>
          <span
            ><strong>{{ 'dairyYieldScan.context.title' | translate }}</strong></span
          >
        </button>
        @for (
          category of questionnaire.categories;
          track category.name;
          let categoryIndex = $index
        ) {
          <button
            type="button"
            class="progress-step"
            [class.active]="activeProgressStep() === categoryIndex + 1"
            [attr.aria-current]="activeProgressStep() === categoryIndex + 1 ? 'step' : null"
            (click)="scrollToSection('scan-category-' + categoryIndex)"
          >
            <span class="progress-dot">{{ categoryIndex + 1 }}</span>
            <span>
              <strong>{{ category.name | translate }}</strong>
              <small>
                {{ answeredInCategory(category) }}/{{ category.questions.length }}
                {{ 'dairyYieldScan.progress.answered' | translate }}
              </small>
            </span>
          </button>
        }
      </nav>

      <form class="scan-content" (submit)="submit($event)" novalidate>
        <div class="page-heading">
          <p class="eyebrow">{{ 'dairyYieldScan.page.eyebrow' | translate }}</p>
          <h1>{{ 'dairyYieldScan.page.title' | translate }}</h1>
          <p>{{ 'dairyYieldScan.page.introduction' | translate }}</p>
        </div>

        <section class="details-section" id="scan-details">
          <div class="section-heading">
            <div>
              <h2>{{ 'dairyYieldScan.context.title' | translate }}</h2>
              <p>{{ 'dairyYieldScan.context.introduction' | translate }}</p>
            </div>
          </div>

          <div class="details-card" [formGroup]="contextForm">
            <h3>{{ 'dairyYieldScan.context.conversation' | translate }}</h3>
            <div class="details-grid compact-grid">
              <mat-form-field appearance="outline">
                <mat-label>{{ 'dairyYieldScan.fields.farmerName' | translate }}</mat-label>
                <input
                  matInput
                  id="context-farmerName"
                  aria-label="{{ 'dairyYieldScan.fields.farmerName' | translate }}"
                  formControlName="farmerName"
                  maxlength="200"
                />
                @if (contextForm.controls.farmerName.hasError('required')) {
                  <mat-error>{{ 'dairyYieldScan.validation.required' | translate }}</mat-error>
                } @else if (contextForm.controls.farmerName.hasError('maxlength')) {
                  <mat-error>{{ 'dairyYieldScan.validation.maximumLength' | translate }}</mat-error>
                }
              </mat-form-field>
              <fieldset
                class="customer-answer"
                role="radiogroup"
                aria-required="true"
                [attr.aria-invalid]="
                  contextForm.controls.isExistingAgrifirmCustomer.touched &&
                  contextForm.controls.isExistingAgrifirmCustomer.invalid
                "
                [attr.aria-describedby]="
                  contextForm.controls.isExistingAgrifirmCustomer.touched &&
                  contextForm.controls.isExistingAgrifirmCustomer.invalid
                    ? 'customer-status-error'
                    : null
                "
              >
                <legend>
                  {{ 'dairyYieldScan.fields.isExistingAgrifirmCustomer' | translate }}
                </legend>
                <label>
                  <input
                    type="radio"
                    id="context-isExistingAgrifirmCustomer"
                    name="isExistingAgrifirmCustomer"
                    formControlName="isExistingAgrifirmCustomer"
                    [value]="true"
                  />{{ 'dairyYieldScan.customer.yes' | translate }}
                </label>
                <label>
                  <input
                    type="radio"
                    id="context-isExistingAgrifirmCustomer-no"
                    name="isExistingAgrifirmCustomer"
                    formControlName="isExistingAgrifirmCustomer"
                    [value]="false"
                  />{{ 'dairyYieldScan.customer.no' | translate }}
                </label>
                @if (
                  contextForm.controls.isExistingAgrifirmCustomer.touched &&
                  contextForm.controls.isExistingAgrifirmCustomer.invalid
                ) {
                  <mat-error id="customer-status-error">{{
                    contextForm.controls.isExistingAgrifirmCustomer.getError('server') ??
                      'dairyYieldScan.validation.customerStatusRequired' | translate
                  }}</mat-error>
                }
              </fieldset>
              @if (contextForm.controls.isExistingAgrifirmCustomer.value === true) {
                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{ 'dairyYieldScan.fields.customerNumber' | translate }}</mat-label>
                  <input
                    matInput
                    id="context-customerNumber"
                    aria-label="{{ 'dairyYieldScan.fields.customerNumber' | translate }}"
                    formControlName="customerNumber"
                    required
                  />
                  @if (contextForm.controls.customerNumber.hasError('required')) {
                    <mat-error id="customer-number-error">{{
                      'dairyYieldScan.validation.required' | translate
                    }}</mat-error>
                  } @else if (contextForm.controls.customerNumber.hasError('maxlength')) {
                    <mat-error id="customer-number-error">{{
                      'dairyYieldScan.validation.maximumLength' | translate
                    }}</mat-error>
                  } @else if (contextForm.controls.customerNumber.hasError('server')) {
                    <mat-error id="customer-number-error">{{
                      contextForm.controls.customerNumber.getError('server') | translate
                    }}</mat-error>
                  }
                </mat-form-field>
                <fieldset
                  class="customer-answer"
                  role="radiogroup"
                  aria-required="true"
                  [attr.aria-invalid]="
                    contextForm.controls.buysAgrifirmCompoundFeed.touched &&
                    contextForm.controls.buysAgrifirmCompoundFeed.invalid
                  "
                  [attr.aria-describedby]="
                    contextForm.controls.buysAgrifirmCompoundFeed.touched &&
                    contextForm.controls.buysAgrifirmCompoundFeed.invalid
                      ? 'customer-feed-error'
                      : null
                  "
                >
                  <legend>
                    {{ 'dairyYieldScan.fields.buysAgrifirmCompoundFeed' | translate }}
                  </legend>
                  <label>
                    <input
                      type="radio"
                      id="context-buysAgrifirmCompoundFeed"
                      name="buysAgrifirmCompoundFeed"
                      formControlName="buysAgrifirmCompoundFeed"
                      [value]="true"
                    />{{ 'dairyYieldScan.customer.yes' | translate }}
                  </label>
                  <label>
                    <input
                      type="radio"
                      id="context-buysAgrifirmCompoundFeed-no"
                      name="buysAgrifirmCompoundFeed"
                      formControlName="buysAgrifirmCompoundFeed"
                      [value]="false"
                    />{{ 'dairyYieldScan.customer.no' | translate }}
                  </label>
                  @if (
                    contextForm.controls.buysAgrifirmCompoundFeed.touched &&
                    contextForm.controls.buysAgrifirmCompoundFeed.invalid
                  ) {
                    <mat-error id="customer-feed-error">{{
                      contextForm.controls.buysAgrifirmCompoundFeed.getError('server') ??
                        'dairyYieldScan.validation.feedAnswerRequired' | translate
                    }}</mat-error>
                  }
                </fieldset>
              }
            </div>

            <h3 class="production-heading">
              {{ 'dairyYieldScan.context.production' | translate }}
            </h3>
            <div class="details-grid">
              <mat-form-field appearance="outline">
                <mat-label>{{ 'dairyYieldScan.fields.dairyCowCount' | translate }}</mat-label>
                <input
                  matInput
                  agrNumber
                  id="context-dairyCowCount"
                  aria-label="{{ 'dairyYieldScan.fields.dairyCowCount' | translate }}"
                  [step]="1"
                  formControlName="dairyCowCount"
                />
                @if (contextForm.controls.dairyCowCount.invalid) {
                  <mat-error>{{
                    'dairyYieldScan.validation.invalidCowCount' | translate
                  }}</mat-error>
                }
              </mat-form-field>

              <h4>{{ 'dairyYieldScan.context.currentSituation' | translate }}</h4>
              <mat-form-field appearance="outline">
                <mat-label>{{
                  'dairyYieldScan.fields.currentMilkProduction' | translate
                }}</mat-label>
                <input
                  matInput
                  agrNumber
                  id="context-currentMilkProduction"
                  aria-label="{{ 'dairyYieldScan.fields.currentMilkProduction' | translate }}"
                  [step]="0.1"
                  (blur)="roundProductionInput('currentMilkProduction')"
                  formControlName="currentMilkProduction"
                />
                <span matTextSuffix>{{ 'dairyYieldScan.units.kilogramsPerDay' | translate }}</span>
                @if (contextForm.controls.currentMilkProduction.invalid) {
                  <mat-error>{{ 'dairyYieldScan.validation.invalidNumber' | translate }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>{{
                  'dairyYieldScan.fields.currentFatPercentage' | translate
                }}</mat-label>
                <input
                  matInput
                  agrNumber
                  id="context-currentFatPercentage"
                  aria-label="{{ 'dairyYieldScan.fields.currentFatPercentage' | translate }}"
                  [step]="0.01"
                  [max]="100"
                  formControlName="currentFatPercentage"
                  (blur)="roundProductionInput('currentFatPercentage')"
                />
                <span matTextSuffix>%</span>
                @if (contextForm.controls.currentFatPercentage.invalid) {
                  <mat-error>{{ 'dairyYieldScan.validation.invalidNumber' | translate }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>{{
                  'dairyYieldScan.fields.currentProteinPercentage' | translate
                }}</mat-label>
                <input
                  matInput
                  agrNumber
                  id="context-currentProteinPercentage"
                  aria-label="{{ 'dairyYieldScan.fields.currentProteinPercentage' | translate }}"
                  [step]="0.01"
                  [max]="100"
                  formControlName="currentProteinPercentage"
                  (blur)="roundProductionInput('currentProteinPercentage')"
                />
                <span matTextSuffix>%</span>
                @if (contextForm.controls.currentProteinPercentage.invalid) {
                  <mat-error>{{ 'dairyYieldScan.validation.invalidNumber' | translate }}</mat-error>
                }
              </mat-form-field>
              <div class="calculated-results" data-test="current-calculated-results">
                <article
                  class="calculated-result"
                  aria-live="polite"
                  aria-labelledby="current-milk-yield"
                >
                  <div class="calculated-result-heading">
                    <mat-icon aria-hidden="true">calculate</mat-icon>
                    <h5 id="current-milk-yield">
                      {{ 'dairyYieldScan.fields.currentMilkYield' | translate }}
                    </h5>
                    <button
                      mat-icon-button
                      type="button"
                      class="meetmelk-explanation-action"
                      [attr.aria-label]="'dairyYieldScan.meetmelkExplanation.ariaLabel' | translate"
                      (click)="openMeetmelkExplanation()"
                    >
                      <mat-icon aria-hidden="true">info</mat-icon>
                    </button>
                  </div>
                  @if (currentMilkYield(); as value) {
                    <output>{{ value | number: '1.1-1' }}</output>
                    <span class="calculated-unit">{{
                      'dairyYieldScan.units.kilogramsPerCowPerDay' | translate
                    }}</span>
                  } @else {
                    <p>{{ 'dairyYieldScan.calculatedField.emptyMeetmelk' | translate }}</p>
                  }
                </article>
                <article
                  class="calculated-result"
                  aria-live="polite"
                  aria-labelledby="current-fat-and-protein-yield"
                >
                  <div class="calculated-result-heading">
                    <mat-icon aria-hidden="true">calculate</mat-icon>
                    <h5 id="current-fat-and-protein-yield">
                      {{ 'dairyYieldScan.fields.currentFatAndProteinYield' | translate }}
                    </h5>
                  </div>
                  @if (currentFatAndProteinYield(); as value) {
                    <output>{{ value | number: '1.1-1' }}</output>
                    <span class="calculated-unit">{{
                      'dairyYieldScan.units.kilogramsPerCowPerDay' | translate
                    }}</span>
                  } @else {
                    <p>{{ 'dairyYieldScan.calculatedField.emptyFatAndProtein' | translate }}</p>
                  }
                </article>
              </div>

              <h4>{{ 'dairyYieldScan.context.targetSituation' | translate }}</h4>
              <mat-form-field appearance="outline">
                <mat-label>{{
                  'dairyYieldScan.fields.targetMilkProduction' | translate
                }}</mat-label>
                <input
                  matInput
                  agrNumber
                  id="context-targetMilkProduction"
                  aria-label="{{ 'dairyYieldScan.fields.targetMilkProduction' | translate }}"
                  [step]="0.1"
                  (blur)="roundProductionInput('targetMilkProduction')"
                  formControlName="targetMilkProduction"
                />
                <span matTextSuffix>{{ 'dairyYieldScan.units.kilogramsPerDay' | translate }}</span>
                @if (contextForm.controls.targetMilkProduction.invalid) {
                  <mat-error>{{ 'dairyYieldScan.validation.invalidNumber' | translate }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>{{ 'dairyYieldScan.fields.targetFatPercentage' | translate }}</mat-label>
                <input
                  matInput
                  agrNumber
                  id="context-targetFatPercentage"
                  aria-label="{{ 'dairyYieldScan.fields.targetFatPercentage' | translate }}"
                  [step]="0.01"
                  [max]="100"
                  formControlName="targetFatPercentage"
                  (blur)="roundProductionInput('targetFatPercentage')"
                />
                <span matTextSuffix>%</span>
                @if (contextForm.controls.targetFatPercentage.invalid) {
                  <mat-error>{{ 'dairyYieldScan.validation.invalidNumber' | translate }}</mat-error>
                }
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>{{
                  'dairyYieldScan.fields.targetProteinPercentage' | translate
                }}</mat-label>
                <input
                  matInput
                  agrNumber
                  id="context-targetProteinPercentage"
                  aria-label="{{ 'dairyYieldScan.fields.targetProteinPercentage' | translate }}"
                  [step]="0.01"
                  [max]="100"
                  formControlName="targetProteinPercentage"
                  (blur)="roundProductionInput('targetProteinPercentage')"
                />
                <span matTextSuffix>%</span>
                @if (contextForm.controls.targetProteinPercentage.invalid) {
                  <mat-error>{{ 'dairyYieldScan.validation.invalidNumber' | translate }}</mat-error>
                }
              </mat-form-field>
              <div class="calculated-results" data-test="target-calculated-results">
                <article
                  class="calculated-result"
                  aria-live="polite"
                  aria-labelledby="target-milk-yield"
                >
                  <div class="calculated-result-heading">
                    <mat-icon aria-hidden="true">calculate</mat-icon>
                    <h5 id="target-milk-yield">
                      {{ 'dairyYieldScan.fields.targetMilkYield' | translate }}
                    </h5>
                    <button
                      mat-icon-button
                      type="button"
                      class="meetmelk-explanation-action"
                      [attr.aria-label]="'dairyYieldScan.meetmelkExplanation.ariaLabel' | translate"
                      (click)="openMeetmelkExplanation()"
                    >
                      <mat-icon aria-hidden="true">info</mat-icon>
                    </button>
                  </div>
                  @if (targetMilkYield(); as value) {
                    <output>{{ value | number: '1.1-1' }}</output>
                    <span class="calculated-unit">{{
                      'dairyYieldScan.units.kilogramsPerCowPerDay' | translate
                    }}</span>
                  } @else {
                    <p>{{ 'dairyYieldScan.calculatedField.emptyMeetmelk' | translate }}</p>
                  }
                </article>
                <article
                  class="calculated-result"
                  aria-live="polite"
                  aria-labelledby="target-fat-and-protein-yield"
                >
                  <div class="calculated-result-heading">
                    <mat-icon aria-hidden="true">calculate</mat-icon>
                    <h5 id="target-fat-and-protein-yield">
                      {{ 'dairyYieldScan.fields.targetFatAndProteinYield' | translate }}
                    </h5>
                  </div>
                  @if (targetFatAndProteinYield(); as value) {
                    <output>{{ value | number: '1.1-1' }}</output>
                    <span class="calculated-unit">{{
                      'dairyYieldScan.units.kilogramsPerCowPerDay' | translate
                    }}</span>
                  } @else {
                    <p>{{ 'dairyYieldScan.calculatedField.emptyFatAndProtein' | translate }}</p>
                  }
                </article>
              </div>
            </div>
          </div>
        </section>

        @for (
          category of questionnaire.categories;
          track category.name;
          let categoryIndex = $index
        ) {
          <section class="question-section" [id]="'scan-category-' + categoryIndex">
            <div class="section-heading">
              <div>
                <h2>{{ category.name | translate }}</h2>
                <p>
                  {{ answeredInCategory(category) }}/{{ category.questions.length }}
                  {{ 'dairyYieldScan.progress.answered' | translate }}
                </p>
              </div>
            </div>

            @for (question of category.questions; track question.question) {
              <article
                class="question"
                [class.unanswered]="isUnanswered(question.question)"
                [id]="'question-' + question.question"
              >
                <div class="question-heading">
                  <h3 [id]="'question-title-' + question.question">
                    {{ question.question + '.title' | translate }}
                  </h3>
                  @if (hasExplanation(question.question)) {
                    <button
                      mat-stroked-button
                      color="primary"
                      type="button"
                      class="explanation-action"
                      (click)="openExplanation(question.question)"
                    >
                      <mat-icon>info</mat-icon>
                      {{ 'dairyYieldScan.explanation.action' | translate }}
                    </button>
                  }
                </div>

                <div
                  class="answer-grid"
                  role="radiogroup"
                  [attr.aria-labelledby]="'question-title-' + question.question"
                  aria-required="true"
                  [attr.aria-invalid]="isUnanswered(question.question) ? 'true' : null"
                  [attr.aria-describedby]="
                    isUnanswered(question.question) ? 'question-error-' + question.question : null
                  "
                >
                  @for (option of answerOptions; track option.value) {
                    <label class="answer-card">
                      <input
                        type="radio"
                        [name]="question.question"
                        [value]="option.value"
                        [formControl]="answerControl(question.question)"
                        required
                      />
                      <span class="answer-visual">
                        <img
                          [src]="answerImagePath(question.question, option.value)"
                          alt=""
                          loading="lazy"
                          (error)="useFallbackAnswerImage($event, option.fallbackImage)"
                        />
                        <span class="answer-overlay" aria-hidden="true"></span>
                        <span class="answer-copy">
                          <strong>{{ option.labelKey | translate }}</strong>
                        </span>
                        @if (answerControl(question.question).value === option.value) {
                          <span class="selected-icon" aria-hidden="true"></span>
                        }
                      </span>
                    </label>
                  }
                </div>

                @if (isUnanswered(question.question)) {
                  <p class="required" [id]="'question-error-' + question.question">
                    {{ 'dairyYieldScan.validation.answerRequired' | translate }}
                  </p>
                }
              </article>
            }
          </section>
        }

        <section class="submit-panel" [agrBlockSpinner]="submitting()">
          <div>
            <p class="eyebrow">{{ 'dairyYieldScan.submit.eyebrow' | translate }}</p>
            <h2>{{ 'dairyYieldScan.submit.title' | translate }}</h2>
            <p>{{ 'dairyYieldScan.submit.description' | translate }}</p>
          </div>
          <button
            mat-flat-button
            color="primary"
            type="submit"
            [disabled]="submitting()"
            data-test="submit-scan"
          >
            {{ 'dairyYieldScan.submit.action' | translate }}
            <mat-icon>arrow_forward</mat-icon>
          </button>
        </section>
      </form>
    }
  </main>
}
